// frontend/src/notifications/groupNotifications.js
/**
 * Group + section notifications for display.
 *
 * Returns:
 * [
 *   { date_label: 'Today', groups: [
 *     { group_key: 'rma:case:42', items: [n1, n2, n3], latest: n1, has_unread: true },
 *     ...
 *   ]},
 *   { date_label: 'Yesterday', groups: [...] },
 *   { date_label: 'Older', groups: [...] },
 * ]
 */

function elementKeyFor(notification) {
  const p = notification.payload || {}
  // Scraper payloads carry `sku` too — branch on domain BEFORE the sku check,
  // or retire/cap notifications group as pricing:<sku> and merge with pricing
  // alerts for the same SKU (mislabeled "Pricing ·" subtitle).
  if (notification.domain === 'scraper' && p.target_id != null) {
    // sku rides along for the subtitle; target_id is the grouping identity
    // (the same SKU exists once per competitor).
    return `scraper:target:${p.target_id}:${p.sku ?? ''}`
  }
  if (p.case_id != null) return `rma:case:${p.case_id}`
  if (p.po_id != null) return `po:${p.po_id}`
  if (p.sku != null) return `pricing:${p.sku}`
  if (p.script_path) return `ops:${p.script_path}`
  if (p.watch_id != null) return `lsm:${p.watch_id}`
  return `single:${notification.id}`
}

function dateLabel(iso) {
  const d = new Date(iso)
  const now = new Date()
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const startYesterday = new Date(startToday.getTime() - 86400000)
  if (d >= startToday) return 'Today'
  if (d >= startYesterday) return 'Yesterday'
  return 'Older'
}

export function groupAndSection(notifications, { onlyUnread = false } = {}) {
  const filtered = onlyUnread
    ? notifications.filter(n => n.seen_at == null)
    : notifications

  // Group within each date bucket
  const dateBuckets = { Today: {}, Yesterday: {}, Older: {} }
  filtered.forEach(n => {
    const d = dateLabel(n.created_at)
    const k = elementKeyFor(n)
    if (!dateBuckets[d][k]) dateBuckets[d][k] = []
    dateBuckets[d][k].push(n)
  })

  const sections = []
  for (const label of ['Today', 'Yesterday', 'Older']) {
    const groups = Object.entries(dateBuckets[label]).map(([group_key, items]) => {
      // Newest first within a group
      items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      return {
        group_key,
        items,
        latest: items[0],
        has_unread: items.some(i => i.seen_at == null),
        unread_count: items.filter(i => i.seen_at == null).length,
      }
    })
    // Sort groups within section by latest item time, newest first
    groups.sort((a, b) => new Date(b.latest.created_at) - new Date(a.latest.created_at))
    if (groups.length > 0) sections.push({ date_label: label, groups })
  }
  return sections
}
